import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FloorOneMap, FloorTwoMap, findRoomIdByHint, getRoomLabel } from './FloorMaps';
import { useMountTransition } from '../hooks/useMountTransition';
import { activateClose, dismissFocus } from '../utils/dismissFocus';
import './styles/RoomMapModal.css';

const guessFloor = (room, fallback = 1) => {
  if (!room) return fallback;
  const n = parseInt(String(room).replace(/[^\d]/g, ''), 10);
  if (!Number.isFinite(n)) return fallback;
  if (n >= 20 && n < 100) return 2;
  return 1;
};

const RoomMapModal = ({ open, room, onClose, initialFloor }) => {
  const { mounted, active } = useMountTransition(open, 320);
  const [floor, setFloor] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);

  const handleClose = useCallback(
    (event) => {
      dismissFocus(event);
      onClose?.();
    },
    [onClose]
  );
  const switchRef = useRef(null);
  const dragRef = useRef({ active: false, startX: 0, width: 0, from: 1 });

  const handleFloor = useCallback(
    (id) => {
      setFloor((prev) => {
        if (prev === id) return prev;
        setSelectedId(findRoomIdByHint(id, room));
        return id;
      });
      setDragX(0);
      setDragging(false);
    },
    [room]
  );

  useEffect(() => {
    if (!open) return;
    const f = initialFloor ?? guessFloor(room, 1);
    setFloor(f);
    setSelectedId(findRoomIdByHint(f, room));
    setDragX(0);
  }, [open, initialFloor, room]);

  useEffect(() => {
    if (!mounted) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
      if (e.key === 'ArrowLeft') handleFloor(1);
      if (e.key === 'ArrowRight') handleFloor(2);
    };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('kkt-dialog-open');
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('kkt-dialog-open');
      document.body.style.overflow = prev;
    };
  }, [mounted, handleClose, handleFloor]);

  const activeId = useMemo(() => {
    if (selectedId) return selectedId;
    return findRoomIdByHint(floor, room);
  }, [selectedId, floor, room]);

  const activeLabel = useMemo(
    () => getRoomLabel(floor, activeId) || (room ? String(room) : null),
    [floor, activeId, room]
  );

  const onPointerDown = (e) => {
    const el = switchRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragRef.current = {
      active: true,
      startX: e.clientX,
      width: rect.width,
      from: floor,
    };
    setDragging(true);
    el.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragRef.current.active) return;
    const half = dragRef.current.width / 2;
    let dx = e.clientX - dragRef.current.startX;
    if (dragRef.current.from === 1) dx = Math.max(0, Math.min(half, dx));
    else dx = Math.max(-half, Math.min(0, dx));
    setDragX(dx);
  };

  const onPointerUp = () => {
    if (!dragRef.current.active) return;
    const { width, from } = dragRef.current;
    const half = width / 2;
    const threshold = half * 0.28;
    dragRef.current.active = false;
    setDragging(false);
    if (from === 1 && dragX > threshold) handleFloor(2);
    else if (from === 2 && dragX < -threshold) handleFloor(1);
    else {
      setDragX(0);
    }
  };

  if (!mounted || typeof document === 'undefined') return null;

  const title = room ? `Кабинет ${room}` : 'Карта кабинетов';
  const routeHint = floor === 1 ? 'Маршрут от входа' : 'Маршрут от лестницы';
  const half = dragRef.current.width ? dragRef.current.width / 2 : 0;
  const thumbShift =
    (floor === 1 ? 0 : 100) + (half ? (dragX / half) * 100 : 0);

  return createPortal(
    <>
      <div
        className={`kkt-blur-backdrop${active ? ' is-visible' : ''}`}
        onClick={handleClose}
        role="presentation"
      />
      <div
        className={`room-map-modal${active ? ' is-visible' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="room-map-title"
      >
        <header className="room-map-modal__head">
          <div className="room-map-modal__head-main">
            <span className="room-map-modal__icon" aria-hidden="true">
              <i className="bi bi-map-fill"></i>
            </span>
            <div>
              <h2 id="room-map-title">{title}</h2>
              <p className="room-map-modal__sub">
                {activeLabel ? (
                  <>
                    <strong>{activeLabel}</strong>
                    <span> · {routeHint}</span>
                  </>
                ) : (
                  'Нажмите кабинет на плане'
                )}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="room-map-modal__close"
            onPointerDown={(event) => activateClose(event, handleClose)}
            onClick={(event) => activateClose(event, handleClose)}
            aria-label="Закрыть"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </header>

        <div
          ref={switchRef}
          className={`room-map-modal__switch${dragging ? ' is-dragging' : ''}`}
          role="tablist"
          aria-label="Этаж"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <span
            className="room-map-modal__switch-thumb"
            style={{ transform: `translateX(${thumbShift}%)` }}
            aria-hidden="true"
          />
          <button
            type="button"
            role="tab"
            aria-selected={floor === 1}
            className={`room-map-modal__switch-btn${floor === 1 ? ' is-active' : ''}`}
            onClick={() => handleFloor(1)}
          >
            1 этаж
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={floor === 2}
            className={`room-map-modal__switch-btn${floor === 2 ? ' is-active' : ''}`}
            onClick={() => handleFloor(2)}
          >
            2 этаж
          </button>
        </div>

        <div className="room-map-modal__canvas">
          <div className="room-map-view" key={floor}>
            {floor === 1 ? (
              <FloorOneMap
                room={room}
                selectedId={selectedId}
                onSelect={(id) => setSelectedId((prev) => (prev === id ? null : id))}
              />
            ) : (
              <FloorTwoMap
                room={room}
                selectedId={selectedId}
                onSelect={(id) => setSelectedId((prev) => (prev === id ? null : id))}
              />
            )}
          </div>
        </div>

        <div className="room-map-modal__foot">
          <button type="button" className="room-map-modal__ok" onClick={handleClose}>
            Понятно
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

export default RoomMapModal;
